import { __, _x } from '@wordpress/i18n';
import { buildQueryString } from '@wordpress/url';
import { searchAddress } from "@public/js/deas-address-search/form-handler";

let autoSearchTimeout;

/**
 * Search input component.
 * @param {Reactive} reactive
 * @returns {String}
 */
export const SearchInput = ( reactive ) => {
	reactive.onRerender = () => {
		const inputElement = reactive.rootElement.querySelector( 'input.deas-address-search__input' );

		inputElement?.addEventListener( 'input', ( event ) => {
			reactive.state = {
				...reactive.state,
				query: event.target.value,
			};
		} );
	};

	return `<input id="addressLookup" class="deas-address-search__input" autocomplete="off" name="query" value="${ reactive.computed.query }">`;
};

/**
 * Add event listener to search result elements.
 * @param {HTMLElement} element
 * @param {Reactive} reactive
 * @param {AddressResult} result
 */
const addSearchResultEventListener = ( element, reactive, result ) => {
	element.addEventListener( 'click', ( event ) => {
		event.preventDefault();
		event.stopImmediatePropagation();
		reactive.state = {
			...reactive.state,
			addressResults: [],
			selectedAddress: result,
			query: result.address ?? reactive.state.query,
		};
	} );
};

/**
 * Single search result component.
 * @param {AddressResult} result
 * @param {Reactive} reactive
 * @returns {String}
 */
export const SearchResult = ( result, reactive ) => {
	reactive.onRerender = () => {
		const element = reactive.rootElement.querySelector( `a.deas-address-search__result[data-result="${ result.id }"]` );
		addSearchResultEventListener( element, reactive, result );
	};

	return `<a href="#" data-result="${ result.id }" class="deas-address-search__result">
              <div>${ result.address }</div>
              <div class="deas-address-search__result__select">${ __( 'Select', 'wp-paperform-integration' ) }</div>
            </a>`;
};

/**
 * Single search results list component.
 * @param {Reactive} reactive
 * @returns {String}
 */
export const SearchResultList = ( reactive ) => {
	return `<div class="deas-address-search__result-list">
              ${ reactive.state.addressResults.map( ( result ) => SearchResult( result, reactive ) ).join( '' ) }
            </div>`;
};

/**
 * Search button component.
 * @returns {String}
 */
export const SearchButton = () => {
	return `<button type="submit" class="deas-address-search__button">
              <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M61.25 61.25L48.5625 48.5625M55.4167 32.0833C55.4167 44.97 44.97 55.4167 32.0833 55.4167C19.1967 55.4167 8.75 44.97 8.75 32.0833C8.75 19.1967 19.1967 8.75 32.0833 8.75C44.97 8.75 55.4167 19.1967 55.4167 32.0833Z"
                        stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </button>`;
};

/**
 * Search help text component.
 * @returns {String}
 */
export const SearchHelpText = () => {
	return `<p class="deas-address-search__help-text">
              ${ __( 'Please start typing your address', 'wp-paperform-integration' ) }
            </p>`;
};

/**
 * Slot content component.
 * @param {Reactive} reactive
 * @returns {String}
 */
export const SlotContent = ( reactive ) => {
	reactive.onRerender = () => {
		reactive.rootElement.querySelector( '[data-paperform-id]' )
			?.setAttribute( 'prefill', buildQueryString( reactive.computed.paperformPrefill ) );
	};

	return reactive.originalHtml;
};

/**
 * Initialize the form handler for address search.
 * @param {Reactive} reactive
 * @param {String} selector
 */
export const initFormHandler = ( reactive, selector ) => {
	const searchForms = [ ...reactive.rootElement.querySelectorAll( selector ) ];

	searchForms.forEach( ( form ) => {
		form.addEventListener( 'submit', async ( event ) => {
			event.preventDefault();
			const results = await searchAddress( form );
			reactive.state = {
				...reactive.state,
				addressResults: results,
			};
		} );

		form.addEventListener( 'input', () => {
			if ( autoSearchTimeout ) {
				clearTimeout( autoSearchTimeout );
			}

			reactive.state = {
				...reactive.state,
				selectedAddress: null,
			};

			autoSearchTimeout = setTimeout( () => form.dispatchEvent( new Event( 'submit' ) ), 750 );
		} );
	} );
}

/**
 * Single search result component.
 * @param {Reactive} reactive
 * @returns {String}
 */
export const SearchForm = ( reactive ) => {
	reactive.onRerender = () => {
		initFormHandler( reactive, 'form.deas-address-search__form' );
	};

	return `<div class="deas-address-search__container ${ reactive.computed.query ? 'deas-address-search__container--empty' : 'deas-address-search__container--searching' }">
              <form action="#" class="deas-address-search__form">
                <label for="addressLookup" class="deas-address-search__label">
                  ${ _x( 'Address', 'Deas.dk address form input label', 'wp-paperform-integration' ) }:*
                </label>
                ${ SearchHelpText() }
                ${ SearchInput( reactive ) }
                ${ SearchButton() }
                ${ SearchResultList( reactive ) }
            </form>
          </div>
          ${ reactive.state.selectedAddress ? SlotContent( reactive ) : '' }`;
};
